import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, FileText, Image as ImageIcon, Calendar, User, Edit, Trash2, X, Users } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import AssetDocumentModal from '../components/AssetDocumentModal';
import AssetModal from '../components/AssetModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ASSET_DOC_CATEGORIES = {
  'libretto_uso_manutenzione': 'Libretto uso e manutenzione',
  'certificato_conformita': 'Certificato conformità',
  'revisione_periodica': 'Revisione/Controllo periodico',
  'documento_consegna': 'Documento di consegna',
  'fattura_ddt': 'Fattura/DDT acquisto',
  'assicurazione': 'Assicurazione',
  'collaudo': 'Collaudo',
  'foto_asset': 'Foto asset',
  'altro': 'Altro'
};

function AssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dettagli');
  const [asset, setAsset] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [assignedEmployees, setAssignedEmployees] = useState([]); // Multiple employees
  const [documents, setDocuments] = useState([]);
  const [complianceItems, setComplianceItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editForm, setEditForm] = useState({ nome_file: '', descrizione: '', categoria: '', data_scadenza: '' });
  
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setCurrentUser(JSON.parse(userStr));
    }
    fetchAssetData();
  }, [id]);
  
  const canManage = currentUser?.role === 'admin' || currentUser?.role === 'hse_manager';

  // Delete document
  const deleteDocument = async (docId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo documento?')) return;
    
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/documents/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setDocuments(documents.filter(d => d._id !== docId));
        alert('Documento eliminato con successo');
      } else {
        const error = await res.json();
        alert(error.detail || 'Errore durante l\'eliminazione');
      }
    } catch (err) {
      console.error('Error deleting document:', err);
      alert('Errore di connessione');
    }
  };

  // Open edit modal
  const openEditDoc = (doc) => {
    setEditingDoc(doc);
    setEditForm({
      nome_file: doc.nome_file || '',
      descrizione: doc.descrizione || '',
      categoria: doc.categoria || 'altro'
    });
  };

  // Update document
  const updateDocument = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    const formData = new FormData();
    formData.append('nome_file', editForm.nome_file);
    formData.append('descrizione', editForm.descrizione);
    
    try {
      const res = await fetch(`${API_URL}/api/documents/${editingDoc._id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const updated = await res.json();
        setDocuments(documents.map(d => d._id === updated._id ? updated : d));
        setEditingDoc(null);
        alert('Documento aggiornato con successo');
      } else {
        const error = await res.json();
        alert(error.detail || 'Errore durante l\'aggiornamento');
      }
    } catch (err) {
      console.error('Error updating document:', err);
      alert('Errore di connessione');
    }
  };
  
  const fetchAssetData = async () => {
    try {
      setLoading(true);
      
      const assetRes = await fetch(`${API_URL}/api/assets/${id}`);
      if (!assetRes.ok) {
        navigate('/assets');
        return;
      }
      const assetData = await assetRes.json();
      setAsset(assetData);
      
      // Fetch assigned employees (support multiple)
      const assignedIds = assetData.assegnato_a_multipli || (assetData.assegnato_a ? [assetData.assegnato_a] : []);
      if (assignedIds.length > 0) {
        const employeePromises = assignedIds.map(empId => 
          fetch(`${API_URL}/api/employees/${empId}`).then(res => res.ok ? res.json() : null)
        );
        const employees = await Promise.all(employeePromises);
        setAssignedEmployees(employees.filter(e => e !== null));
        // Keep single employee for backward compatibility
        if (employees[0]) setEmployee(employees[0]);
      }
      
      // Fetch documents
      const docsRes = await fetch(`${API_URL}/api/documents?entity_type=asset&entity_id=${id}`);
      const docsData = await docsRes.json();
      setDocuments(docsData);
      
      // Fetch compliance items for asset (for expiration tracking)
      const compRes = await fetch(`${API_URL}/api/compliance?employee_id=asset_${id}`);
      const compData = await compRes.json();
      setComplianceItems(compData);
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching asset data:', err);
      setLoading(false);
    }
  };
  
  // Delete asset
  const handleDeleteAsset = async () => {
    try {
      setDeleting(true);
      const token = localStorage.getItem('token');
      
      const res = await fetch(`${API_URL}/api/assets/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        navigate('/assets');
      } else {
        const error = await res.json();
        alert(error.detail || 'Errore durante l\'eliminazione dell\'asset');
      }
    } catch (err) {
      console.error('Error deleting asset:', err);
      alert('Errore durante l\'eliminazione dell\'asset');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };
  
  const getCategoryLabel = (cat) => {
    return ASSET_DOC_CATEGORIES[cat] || cat;
  };
  
  // Get compliance item for a document (by matching categoria)
  const getComplianceForDoc = (doc) => {
    return complianceItems.find(c => 
      c.categoria === doc.categoria && 
      c.titolo === doc.nome_file
    ) || complianceItems.find(c => c.categoria === doc.categoria);
  };
  
  // Urgency badge helper
  const getUrgencyBadgeClass = (level) => {
    if (level === 'expired' || level === 'critical') return 'badge-critical';
    if (level === 'superseded') return 'badge-secondary';
    if (level === 'warning') return 'badge-warning';
    if (level === 'soon') return 'badge-soon';
    return 'badge-ok';
  };
  
  const getUrgencyLabel = (item) => {
    if (!item || item.days_remaining === null || item.days_remaining === undefined) return null;
    if (item.urgency_level === 'superseded') return 'Superato';
    if (item.days_remaining < 0) return 'Scaduto';
    return `${item.days_remaining}g`;
  };
  
  if (loading) {
    return (
      <div className="loading" data-testid="loading">
        <p>Caricamento asset...</p>
      </div>
    );
  }
  
  if (!asset) {
    return (
      <div className="empty-state">
        <p>Asset non trovato</p>
      </div>
    );
  }
  
  const photos = documents.filter(d => d.categoria === 'foto_asset');
  const docs = documents.filter(d => d.categoria !== 'foto_asset');
  
  return (
    <div data-testid="asset-detail-page">
      <div className="mb-6">
        <Link to="/assets" className="btn btn-secondary mb-4" data-testid="back-button">
          <ArrowLeft size={20} />
          Torna agli Asset
        </Link>
        
        <div className="card">
          <div className="flex items-start gap-6">
            <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
              <span className="text-4xl">
                {asset.tipo === 'mezzo' ? '🚗' : '🔧'}
              </span>
            </div>
            
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-3xl font-bold mb-2">{asset.nome}</h1>
                  <div className="flex gap-3 mb-3">
                    <span className="badge badge-secondary">{asset.tipo}</span>
                    <span className="badge badge-ok">{asset.stato}</span>
                  </div>
                  {asset.descrizione && (
                    <p className="text-muted-foreground mb-3">{asset.descrizione}</p>
                  )}
                  <div className="flex flex-wrap gap-4 text-sm">
                    {asset.marca && <span>🏭 Marca: <strong>{asset.marca}</strong></span>}
                    {asset.modello && <span>📋 Modello: <strong>{asset.modello}</strong></span>}
                    {asset.targa && <span>🚘 Targa: <strong className="font-mono">{asset.targa}</strong></span>}
                    {asset.matricola && <span>🔢 Matricola: <strong className="font-mono">{asset.matricola}</strong></span>}
                  </div>
                </div>
                
                {/* Edit/Delete buttons */}
                {canManage && (
                  <div className="flex gap-2">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setShowEditModal(true)}
                      data-testid="edit-asset-btn"
                    >
                      <Edit size={16} />
                      Modifica
                    </button>
                    <button
                      className="btn btn-secondary text-destructive"
                      onClick={() => setShowDeleteConfirm(true)}
                      data-testid="delete-asset-btn"
                    >
                      <Trash2 size={16} />
                      Elimina
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Assigned employees - support multiple */}
      {assignedEmployees.length > 0 && (
        <div className="card mb-6 bg-gradient-to-r from-blue-50 to-cyan-50 border-primary/20">
          <div className="flex items-center gap-4">
            {assignedEmployees.length > 1 ? <Users size={24} className="text-primary" /> : <User size={24} className="text-primary" />}
            <div>
              <p className="text-sm text-muted-foreground">
                Assegnato a {assignedEmployees.length > 1 ? `(${assignedEmployees.length} persone)` : ''}
              </p>
              <div className="flex flex-wrap gap-2">
                {assignedEmployees.map((emp, idx) => (
                  <Link 
                    key={emp._id}
                    to={`/employees/${emp._id}`}
                    className="text-lg font-semibold text-primary hover:underline"
                  >
                    {emp.nome} {emp.cognome}{idx < assignedEmployees.length - 1 ? ',' : ''}
                  </Link>
                ))}
              </div>
              {asset.data_assegnazione && (
                <p className="text-sm text-muted-foreground">
                  dal {format(new Date(asset.data_assegnazione), 'dd/MM/yyyy', { locale: it })}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      
      <div className="mb-6">
        <div className="flex gap-2 border-b border-border">
          {[
            { id: 'dettagli', label: 'Dettagli', testId: 'tab-dettagli' },
            { id: 'documenti', label: 'Documenti', badge: docs.length, testId: 'tab-documenti' },
            { id: 'foto', label: 'Foto', badge: photos.length, testId: 'tab-foto' }
          ].map((tab) => (
            <button
              key={tab.id}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setActiveTab(tab.id)}
              data-testid={tab.testId}
            >
              {tab.label}
              {tab.badge > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-primary text-primary-foreground">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
      
      <div className="card">
        {activeTab === 'dettagli' && (
          <div data-testid="tab-content-dettagli">
            <h2 className="text-xl font-semibold mb-4">Informazioni Asset</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Nome</label>
                <p className="text-base font-medium mt-1">{asset.nome}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-muted-foreground">Tipo</label>
                <p className="text-base font-medium mt-1">{asset.tipo}</p>
              </div>
              
              {asset.marca && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Marca</label>
                  <p className="text-base font-medium mt-1">{asset.marca}</p>
                </div>
              )}
              
              {asset.modello && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Modello</label>
                  <p className="text-base font-medium mt-1">{asset.modello}</p>
                </div>
              )}
              
              {asset.matricola && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Matricola/Seriale</label>
                  <p className="text-base font-medium mt-1 font-mono">{asset.matricola}</p>
                </div>
              )}
              
              {asset.targa && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Targa</label>
                  <p className="text-base font-medium mt-1 font-mono">{asset.targa}</p>
                </div>
              )}
              
              {asset.data_acquisto && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Data Acquisto</label>
                  <p className="text-base font-medium mt-1">
                    {format(new Date(asset.data_acquisto), 'dd/MM/yyyy', { locale: it })}
                  </p>
                </div>
              )}
              
              <div>
                <label className="text-sm font-medium text-muted-foreground">Stato</label>
                <p className="text-base font-medium mt-1">{asset.stato}</p>
              </div>
              
              {asset.data_prossima_revisione && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Prossima Revisione</label>
                  <p className="text-base font-medium mt-1">
                    {format(new Date(asset.data_prossima_revisione), 'dd/MM/yyyy', { locale: it })}
                  </p>
                </div>
              )}
              
              {asset.data_prossima_manutenzione && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Prossima Manutenzione</label>
                  <p className="text-base font-medium mt-1">
                    {format(new Date(asset.data_prossima_manutenzione), 'dd/MM/yyyy', { locale: it })}
                  </p>
                </div>
              )}
              
              {asset.descrizione && (
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-muted-foreground">Descrizione</label>
                  <p className="text-base mt-1">{asset.descrizione}</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {activeTab === 'documenti' && (
          <div data-testid="tab-content-documenti">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Documenti</h2>
              <button
                className="btn btn-primary"
                onClick={() => setShowDocumentModal(true)}
                data-testid="add-document-button"
              >
                <Plus size={20} />
                Carica Documento
              </button>
            </div>

            {docs.length === 0 ? (
              <div className="empty-state">
                <FileText className="empty-state-icon" />
                <p>Nessun documento caricato</p>
                <button 
                  className="btn btn-primary mt-4"
                  onClick={() => setShowDocumentModal(true)}
                >
                  <Plus size={20} />
                  Carica Primo Documento
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {docs.map((doc) => {
                  const compliance = getComplianceForDoc(doc);
                  const urgencyLabel = getUrgencyLabel(compliance);
                  return (
                  <div 
                    key={doc._id}
                    className="border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
                    data-testid="document-item"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <FileText size={24} className="text-primary mt-1" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h4 className="font-semibold">{doc.nome_file}</h4>
                            <span className="badge badge-secondary text-xs">
                              {getCategoryLabel(doc.categoria)}
                            </span>
                            {/* Show expiration badge if available */}
                            {urgencyLabel && (
                              <span className={`badge text-xs ${getUrgencyBadgeClass(compliance?.urgency_level)}`}>
                                {urgencyLabel}
                              </span>
                            )}
                          </div>
                          {doc.descrizione && (
                            <p className="text-sm text-muted-foreground mb-2">{doc.descrizione}</p>
                          )}
                          <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                            <span>{(doc.dimensione / 1024).toFixed(0)} KB</span>
                            <span>•</span>
                            <span>Caricato: {format(new Date(doc.created_at), 'dd/MM/yyyy', { locale: it })}</span>
                            {/* Show expiration date if available */}
                            {compliance?.data_scadenza && (
                              <>
                                <span>•</span>
                                <span>⏰ Scadenza: {format(new Date(compliance.data_scadenza), 'dd/MM/yyyy', { locale: it })}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <a
                          href={`${API_URL}/api/download/${doc._id}`}
                          className="btn btn-secondary btn-sm"
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="download-document"
                        >
                          Scarica
                        </a>
                        {canManage && (
                          <>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => openEditDoc(doc)}
                              title="Modifica"
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              className="btn btn-secondary btn-sm text-destructive"
                              onClick={() => deleteDocument(doc._id)}
                              title="Elimina"
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'foto' && (
          <div data-testid="tab-content-foto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Foto</h2>
              {canManage && (
                <button
                  className="btn btn-primary"
                  onClick={() => setShowDocumentModal(true)}
                  data-testid="add-photo-button"
                >
                  <Plus size={20} />
                  Carica Foto
                </button>
              )}
            </div>

            {photos.length === 0 ? (
              <div className="empty-state">
                <ImageIcon className="empty-state-icon" />
                <p>Nessuna foto caricata</p>
                {canManage && (
                  <button 
                    className="btn btn-primary mt-4"
                    onClick={() => setShowDocumentModal(true)}
                  >
                    <Plus size={20} />
                    Carica Prima Foto
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {photos.map((photo) => (
                  <div 
                    key={photo._id}
                    className="border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow group"
                    data-testid="photo-item"
                  >
                    <a
                      href={`${API_URL}/api/download/${photo._id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block relative"
                    >
                      <div className="aspect-video bg-gradient-to-br from-muted to-muted/50 relative overflow-hidden">
                        <img 
                          src={`${API_URL}/api/download/${photo._id}`}
                          alt={photo.descrizione || photo.nome_file}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="absolute inset-0 items-center justify-center hidden">
                          <ImageIcon size={48} className="text-muted-foreground" />
                        </div>
                      </div>
                    </a>
                    <div className="p-3 flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium mb-1 truncate">{photo.descrizione || photo.nome_file}</h4>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(photo.created_at), 'dd/MM/yyyy', { locale: it })}
                        </p>
                      </div>
                      {canManage && (
                        <button
                          className="btn btn-secondary btn-sm text-destructive opacity-0 group-hover:opacity-100 transition-opacity ml-2"
                          onClick={(e) => {
                            e.preventDefault();
                            deleteDocument(photo._id);
                          }}
                          title="Elimina foto"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      
      {showDocumentModal && (
        <AssetDocumentModal
          assetId={id}
          onClose={(shouldRefresh) => {
            setShowDocumentModal(false);
            if (shouldRefresh) {
              fetchAssetData();
            }
          }}
        />
      )}

      {/* Edit Document Modal */}
      {editingDoc && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-md w-full">
            <div className="border-b border-border px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold">Modifica Documento</h2>
              <button onClick={() => setEditingDoc(null)} className="btn btn-secondary p-2">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={updateDocument} className="p-6 space-y-4">
              <div className="form-group">
                <label className="form-label">Nome File</label>
                <input
                  type="text"
                  className="form-input"
                  value={editForm.nome_file}
                  onChange={(e) => setEditForm({...editForm, nome_file: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Descrizione</label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={editForm.descrizione}
                  onChange={(e) => setEditForm({...editForm, descrizione: e.target.value})}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button type="button" className="btn btn-secondary" onClick={() => setEditingDoc(null)}>
                  Annulla
                </button>
                <button type="submit" className="btn btn-primary">
                  Salva
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Asset Modal */}
      {showEditModal && (
        <AssetModal 
          asset={asset} 
          onClose={(updated) => {
            setShowEditModal(false);
            if (updated) fetchAssetData();
          }} 
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-semibold mb-4 text-destructive">
              <Trash2 className="inline mr-2" size={20} />
              Elimina Asset
            </h2>
            <p className="text-muted-foreground mb-6">
              Sei sicuro di voler eliminare <strong>"{asset.nome}"</strong>?<br />
              Questa azione eliminerà anche tutti i documenti e le foto associate.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Annulla
              </button>
              <button
                className="btn bg-destructive text-white hover:bg-destructive/90"
                onClick={handleDeleteAsset}
                disabled={deleting}
              >
                {deleting ? 'Eliminazione...' : 'Elimina Definitivamente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AssetDetail;
